/**
 * Test CCXT Pro WebSocket with /pairs/standby
 * Uses parallel watchOrderBook() calls
 */

import { useState, useRef, useEffect } from 'react';
import { Box, Button, Typography, Paper, CircularProgress, Select, MenuItem, Chip } from '@mui/material';
import { Exchange } from '../types/futuresSpot';
import { toCcxtSymbol } from '../utils/symbolConverter';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

type ExchangeKey = 'upbit' | 'bithumb' | 'binance';

export default function TestCcxtWebSocketPage() {
  const [loading, setLoading] = useState(false);
  const [exchange, setExchange] = useState<ExchangeKey>('upbit');
  const [pairs, setPairs] = useState<Record<ExchangeKey, string[]>>({ upbit: [], bithumb: [], binance: [] });
  const [results, setResults] = useState<{ symbol: string; ccxt: string; bid: number | null; ask: number | null; error?: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const clientRef = useRef<any>(null);

  // Fetch pairs on mount
  useEffect(() => {
    fetch(`${API_URL}/pairs/standby`)
      .then(r => r.json())
      .then(data => {
        setPairs({
          binance: data.data?.binance?.pairs ?? [],
          upbit: data.data?.upbit?.pairs ?? [],
          bithumb: data.data?.bithumb?.pairs ?? [],
        });
      })
      .catch(e => setError(String(e)));
  }, []);

  const getExchangeEnum = (key: ExchangeKey): Exchange => {
    return key === 'binance' ? Exchange.Binance : key === 'upbit' ? Exchange.Upbit : Exchange.Bithumb;
  };

  const runTest = async () => {
    setLoading(true);
    setResults([]);
    setError(null);

    try {
      const symbols = pairs[exchange];
      if (!symbols.length) throw new Error('No pairs');

      // Convert to CCXT format
      const converted = symbols.map(s => ({ symbol: s, ccxt: toCcxtSymbol(s, getExchangeEnum(exchange)) }));
      console.log('[Test] Converted:', converted.slice(0, 5));

      // Load CCXT
      const ccxt = (await import('ccxt') as any).default ?? await import('ccxt');
      const Client = ccxt.pro?.[exchange];
      if (!Client) throw new Error(`${exchange} not in ccxt.pro`);

      const client = new Client({ enableRateLimit: true });
      clientRef.current = client;

      // Parallel watchOrderBook calls
      const promises = converted.map(async ({ symbol, ccxt: ccxtSymbol }) => {
        try {
          const ob = await client.watchOrderBook(ccxtSymbol, 5);
          return { symbol, ccxt: ccxtSymbol, bid: ob.bids[0]?.[0] ?? null, ask: ob.asks[0]?.[0] ?? null };
        } catch (e) {
          return { symbol, ccxt: ccxtSymbol, bid: null, ask: null, error: String(e).slice(0, 80) };
        }
      });

      const res = await Promise.all(promises);
      setResults(res);

      // Connection info
      const clients = (client as any).clients || {};
      console.log('[Test] WebSocket connections:', Object.keys(clients).length);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  const cleanup = () => {
    clientRef.current?.close?.();
    clientRef.current = null;
    setResults([]);
  };

  useEffect(() => () => cleanup(), []);

  const success = results.filter(r => !r.error).length;
  const errors = results.filter(r => r.error).length;

  return (
    <Box sx={{ p: 3, maxWidth: 1000, margin: '0 auto' }}>
      <Typography variant="h5" gutterBottom>CCXT WebSocket Test</Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <Select value={exchange} onChange={e => setExchange(e.target.value as ExchangeKey)} size="small">
          <MenuItem value="upbit">Upbit ({pairs.upbit.length})</MenuItem>
          <MenuItem value="bithumb">Bithumb ({pairs.bithumb.length})</MenuItem>
          <MenuItem value="binance">Binance ({pairs.binance.length})</MenuItem>
        </Select>
        <Button variant="contained" onClick={runTest} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Test'}
        </Button>
        <Button variant="outlined" onClick={cleanup}>Close</Button>
      </Box>

      {error && <Paper sx={{ p: 1, mb: 2, bgcolor: 'error.light' }}><Typography color="error">{error}</Typography></Paper>}

      {results.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip label={`✅ ${success}`} color="success" size="small" />
            <Chip label={`❌ ${errors}`} color="error" size="small" />
          </Box>
          <Box sx={{ maxHeight: 500, overflow: 'auto', fontSize: '0.8rem' }}>
            {results.map((r, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2, py: 0.5, borderBottom: '1px solid #eee' }}>
                <Typography sx={{ width: 20 }}>{r.error ? '❌' : '✅'}</Typography>
                <Typography sx={{ width: 100 }}>{r.symbol}</Typography>
                <Typography sx={{ width: 100, color: 'text.secondary' }}>{r.ccxt}</Typography>
                <Typography sx={{ flex: 1 }}>{r.error || `${r.bid?.toLocaleString()} / ${r.ask?.toLocaleString()}`}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
